import React, { useState } from 'react';
import {
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface PillProps extends PressableAnalyticsProps {
  /** this prop will show the pill selected or not, for selected pill, pass true */
  isSelected?: boolean;
  /** this is a primary text and required to pass */
  labelText: string;
  /** the icon id to display. if null no icon will be displayed. */
  iconKey?: string;
  /** optional icon color override */
  iconColor?: string;
  imageSource?: ImageSourcePropType;
  /** this is optional prop and completely based on the response */
  isDisabled?: boolean;
  onPress: () => void;
  testID?: string;
}

interface ComposableStyle {
  pillStyle: ViewStyle;
  textStyle: TextStyle;
}

interface ComposableStyles {
  default: ComposableStyle;
  hover: ComposableStyle;
  press: ComposableStyle;
  focus: ComposableStyle;
  disable: ComposableStyle;
}

function Pill({
  isSelected = false,
  labelText,
  iconKey,
  iconColor = theme.color.text.primary,
  imageSource,
  isDisabled = false,
  analyticsId = '',
  customAttributes = null,
  onPress,
  testID = '',
}: PillProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  const analytics = usePressableAnalytics({ analyticsId, customAttributes });

  const computedStyles = composePillStyles(
    isSelected,
    isHovered,
    isPressed,
    isFocused,
    isDisabled,
  );

  const setIconColor = () => {
    if (isSelected) {
      return theme.color.text.inverse;
    }
    return isDisabled ? theme.color.text.muted : iconColor;
  };
  const renderIcon = () => {
    if (imageSource && !imageLoadError) {
      return (
        <View style={styles.iconStyle}>
          <Image
            source={imageSource}
            style={styles.imageStyle}
            resizeMode="contain"
            onError={() => setImageLoadError(true)}
          />
        </View>
      );
    }

    if (iconKey) {
      return (
        <View style={styles.iconStyle}>
          <Icon iconKey={iconKey} iconColor={setIconColor()} />
        </View>
      );
    }

    return null;
  };

  const onPillPress = () => {
    return onPress();
  };
  return (
    <View style={styles.viewStyle}>
      {labelText ? (
        <Pressable
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setIsPressed(false)}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          style={[styles.pillDefaultStyle, computedStyles.pillStyle]}
          onPress={() => {
            analytics();
            onPillPress();
          }}
          role="button"
          testID={testID}
        >
          {renderIcon()}
          <BodySmBoldText
            numberOfLines={1}
            style={[styles.textDefaultStyle, computedStyles.textStyle]}
          >
            {labelText}
          </BodySmBoldText>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  iconStyle: {
    height: theme.spacing.l,
    width: theme.spacing.l,
  },
  imageStyle: {
    height: 24,
    width: 24,
  },
  pillDefaultStyle: {
    alignItems: 'center',
    borderRadius: theme.spacing.l,
    flexDirection: 'row',
    gap: theme.spacing.s,
    height: 40,
    maxWidth: 200,
    paddingHorizontal: theme.spacing.m,
  },
  textDefaultStyle: {
    flexShrink: 1,
  },
  viewStyle: {
    alignItems: 'center',
  },
});

const commonPillStyle = {
  backgroundColor: theme.color.interactive.inverse,
  borderWidth: 1,
};

const commonUnselectedPillTextStyle = {
  color: theme.color.text.primary,
};

const unselectedPillStyle: ComposableStyles = {
  default: StyleSheet.create({
    pillStyle: {
      ...commonPillStyle,
      borderColor: theme.color.border.primarySolid,
    },
    textStyle: commonUnselectedPillTextStyle,
  }),
  hover: StyleSheet.create({
    pillStyle: {
      ...commonPillStyle,
      borderColor: theme.color.interactive.blackHovered,
    },
    textStyle: commonUnselectedPillTextStyle,
  }),
  press: StyleSheet.create({
    pillStyle: {
      ...commonPillStyle,
      borderColor: theme.color.interactive.blackPressed,
    },
    textStyle: commonUnselectedPillTextStyle,
  }),
  focus: StyleSheet.create({
    pillStyle: {
      ...commonPillStyle,
      borderColor: theme.color.border.focused,
    },
    textStyle: commonUnselectedPillTextStyle,
  }),
  disable: StyleSheet.create({
    pillStyle: {
      ...commonPillStyle,
      borderColor: theme.color.interactive.disabled.border,
    },
    textStyle: {
      color: theme.color.interactive.disabled.text,
    },
  }),
};

const commonSelectedPillTextStyle = {
  color: theme.color.text.inverse,
};

const commonSelectedPillStyle = {
  borderColor: theme.color.background.primary,
  borderWidth: 1,
};

const selectedPillStyle: ComposableStyles = {
  default: StyleSheet.create({
    pillStyle: {
      ...commonSelectedPillStyle,
      backgroundColor: theme.color.interactive.black,
    },
    textStyle: commonSelectedPillTextStyle,
  }),
  hover: StyleSheet.create({
    pillStyle: {
      ...commonSelectedPillStyle,
      backgroundColor: theme.color.interactive.blackHovered,
    },
    textStyle: commonSelectedPillTextStyle,
  }),
  press: StyleSheet.create({
    pillStyle: {
      ...commonSelectedPillStyle,
      backgroundColor: theme.color.interactive.blackPressed,
    },
    textStyle: commonSelectedPillTextStyle,
  }),
  focus: StyleSheet.create({
    pillStyle: {
      ...commonSelectedPillStyle,
      backgroundColor: theme.color.interactive.blackPressed,
    },
    textStyle: commonSelectedPillTextStyle,
  }),
  /* eslint-disable react-native/no-unused-styles -- dynamic style keys require disabling the rule */
  disable: StyleSheet.create({
    pillStyle: {
      ...commonSelectedPillStyle,
      backgroundColor: theme.color.interactive.disabled.background,
    },

    textStyle: {
      color: theme.color.interactive.disabled.text,
    },
  }),
  /* eslint-enable react-native/no-unused-styles */
};

const composePillStyles = (
  isPillSelected,
  isHovered,
  isPressed,
  isFocused,
  isDisabled,
): ComposableStyle => {
  const pillStyle = isPillSelected ? selectedPillStyle : unselectedPillStyle;

  if (isDisabled) {
    return {
      pillStyle: pillStyle.disable.pillStyle,
      textStyle: pillStyle.disable.textStyle,
    };
  } else if (isHovered) {
    return {
      pillStyle: pillStyle.hover.pillStyle,
      textStyle: pillStyle.hover.textStyle,
    };
  } else if (isPressed) {
    return {
      pillStyle: pillStyle.press.pillStyle,
      textStyle: pillStyle.press.textStyle,
    };
  } else if (isFocused) {
    return {
      pillStyle: pillStyle.focus.pillStyle,
      textStyle: pillStyle.focus.textStyle,
    };
  }

  return {
    pillStyle: pillStyle.default.pillStyle,
    textStyle: pillStyle.default.textStyle,
  };
};

export default Pill;
